import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark, Button as NxButton, Card as NxCard } from "@nxivo/ui";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { resolveNxivoAdmin } from "@/lib/nxivo";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

function LoginPageContent() {
  const { login, loginWithGoogleToken, beginMicrosoftLogin, completeOAuthLogin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const nxivoAdmin = resolveNxivoAdmin();
  const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.documentElement.setAttribute("data-color-mode", "dark");
  }, []);

  useEffect(() => {
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    if (error) {
      toast.error(decodeURIComponent(error));
      setSearchParams({});
      return;
    }

    if (!token) return;

    const handleOAuthToken = async () => {
      try {
        await completeOAuthLogin(token);
        toast.success("Signed in successfully");
      } catch {
        toast.error("OAuth login failed");
      } finally {
        setSearchParams({});
      }
    };

    handleOAuthToken();
  }, [searchParams, setSearchParams, completeOAuthLogin]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Welcome back!");
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: { credential?: string }) => {
    if (!credentialResponse.credential) {
      toast.error("Google authentication failed");
      return;
    }

    setLoading(true);
    try {
      await loginWithGoogleToken(credentialResponse.credential);
      toast.success("Signed in successfully");
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Google authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNxivoContinue = async () => {
    if (!nxivoAdmin) return;
    setLoading(true);
    try {
      const launchRes = await fetch(`${nxivoAdmin.api}/api/auth/silent-launch`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appKey: "bindley" }),
      });
      if (launchRes.ok) {
        const launch = await launchRes.json();
        if (launch.launchUrl) {
          window.location.assign(launch.launchUrl);
          return;
        }
        const sessionRes = await fetch(
          `${(process.env.REACT_APP_BACKEND_URL || "http://localhost:8001").replace(/\/+$/, "")}/api/auth/session`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              accessToken: launch.tokens?.accessToken,
              refreshToken: launch.tokens?.refreshToken,
            }),
          },
        );
        const body = await sessionRes.json().catch(() => ({}));
        if (!sessionRes.ok) throw new Error(body.detail || "Nxivo session failed");
        await completeOAuthLogin(body.token, body.user);
        toast.success("Signed in via Nxivo");
        return;
      }
      window.location.assign(`${nxivoAdmin.web}/login`);
    } catch (err: any) {
      toast.error(err?.message || "Nxivo sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-[100dvh] items-center justify-center p-6"
      style={{
        background: "radial-gradient(circle at top, var(--login-wash-from), var(--login-wash-to) 55%)",
      }}
      data-testid="login-page"
    >
      <NxCard className="w-full max-w-md" title="Bindley" subtitle="Library management — sign in to continue">
        <div className="mb-5 flex flex-col items-center gap-2">
          <BrandMark size={56} product="bindley" title="Bindley" />
          <p className="m-0 text-sm text-[var(--text-muted)]">Every book, bind together.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              data-testid="login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              className="bg-[var(--bg)] border-[var(--border)]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="bg-[var(--bg)] border-[var(--border)] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <NxButton type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
            {loading ? "Signing in..." : "Sign In"}
          </NxButton>
        </form>

        <div className="my-4 flex items-center gap-3">
          <div className="h-px flex-1 bg-[var(--border)]" />
          <span className="text-xs text-[var(--text-muted)]">OR</span>
          <div className="h-px flex-1 bg-[var(--border)]" />
        </div>

        <div className="flex flex-col gap-3">
          {nxivoAdmin && (
            <NxButton type="button" disabled={loading} onClick={handleNxivoContinue} className="w-full">
              {loading ? "Signing in..." : "Continue with Nxivo"}
            </NxButton>
          )}
          {GOOGLE_CLIENT_ID && (
            <div className="flex justify-center">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error("Google authentication failed")} />
            </div>
          )}
          <NxButton type="button" variant="secondary" className="w-full" onClick={beginMicrosoftLogin} disabled={loading}>
            Sign in with Microsoft
          </NxButton>
        </div>

        {isLocalHost && (
          <div className="mt-5 rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-[var(--bg)] p-3 text-xs text-[var(--text-muted)]">
            <p className="m-0 font-semibold text-[var(--text)]">Local demo</p>
            <p className="m-0 mt-1 font-mono">admin / admin1234</p>
          </div>
        )}
      </NxCard>
    </div>
  );
}

function LoginPageWithGoogleProvider() {
  if (!GOOGLE_CLIENT_ID) return <LoginPageContent />;

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginPageContent />
    </GoogleOAuthProvider>
  );
}

export default LoginPageWithGoogleProvider;
